import ThemeContext from "config/context/themeContext";
import styles from "css/plugins.module.css";
import { useCallback, useContext, useEffect, useState } from "react";
import PluginsMain from "../components/plugins/PluginsMain";
import GuiContext from "../config/context/guiContext";
import Loader from "../components/shared/loader/Loader";
import useTitle from "../config/useTitle";

/**
 * Jedan POST ka backendu, sa poštenim čitanjem odgovora.
 *
 * ZAŠTO NE `response.json()` ODMAH: kad servis ne radi, nginx vraća SVOJU
 * stranicu greške — HTML, ne JSON. `json()` tada pukne sa „unexpected
 * character at line 1 column 1", a to je poruka o prvom znaku `<`, ne o
 * uzroku. Zato se telo prvo pročita kao tekst, pa tek onda pokuša razumeti.
 */
const posalji = async (url, telo) => {
  let odgovor;
  try {
    odgovor = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(telo),
    });
  } catch {
    // Kad servis ne radi, veza se ODBIJA — `fetch` baci i odgovora nema, pa
    // `response.ok` nikad ne dođe na red. Pregledač to zove „NetworkError when
    // attempting to fetch resource", što korisniku ne znači ništa.
    throw new Error("the GUI server could not be reached");
  }

  const tekst = await odgovor.text();
  let podaci = null;
  try {
    podaci = tekst ? JSON.parse(tekst) : null;
  } catch {
    podaci = null; // odgovor nije JSON — npr. nginx-ova 502 stranica
  }

  if (!odgovor.ok) {
    const razlog =
      podaci?.details?.[0]?.msg ||
      podaci?.message ||
      (odgovor.status === 502
        ? "the GUI server is not responding"
        : `the server answered ${odgovor.status}`);
    throw new Error(razlog);
  }
  return podaci;
};

const Plugins = () => {
  useTitle("Plugins");
  const { dark: isDark } = useContext(ThemeContext);

  const {
    URL,
    userData: { guiname, id },
    selected: { selectedServer },
  } = useContext(GuiContext);

  const [plugins, setPlugins] = useState(null);
  const [userPlugins, setUserPlugins] = useState(null);
  const [greska, setGreska] = useState(null);
  const [ucitavam, setUcitavam] = useState(true);

  /**
   * Oba spiska se traže zajedno, jer strana bez ijednog od njih ne može da se
   * prikaže. `useCallback` da sme u zavisnosti effect-a — bez toga se funkcija
   * pravi iznova pri svakom iscrtavanju, effect je opet zove, i vrti se u krug.
   */
  const ucitaj = useCallback(async () => {
    setUcitavam(true);
    setGreska(null);
    try {
      const [svi, korisnikovi] = await Promise.all([
        posalji(`${URL}/api/plugins/all`, { server_url: selectedServer }),
        posalji(`${URL}/api/plugins/user`, {
          guiname,
          server_url: selectedServer,
          account_id: id,
        }),
      ]);
      setPlugins(svi);
      setUserPlugins(korisnikovi);
    } catch (e) {
      // Do 28.09.2026. se ovde samo pisalo u konzolu, a stanje ostajalo prazno.
      // Deca su tada videla `undefined` i prikazivala Loader — bez kraja i bez
      // reči o tome šta ne valja. Izmereno na TAS-u dok je servis bio ugašen:
      // svaki poziv 502, a na ekranu večiti „Busy".
      setGreska(e.message);
    } finally {
      setUcitavam(false);
    }
  }, [URL, selectedServer, guiname, id]);

  useEffect(() => {
    ucitaj();
  }, [ucitaj]);

  // Dok ničega nema, strana čeka. Pri osvežavanju posle Install/Apply podaci
  // ostaju na ekranu — dugme koje je pritisnuto ima svoj brojač.
  if (ucitavam && !plugins) {
    return (
      <div className={`${isDark ? styles.dark : ""} container`}>
        <Loader />
      </div>
    );
  }

  if (greska) {
    return (
      <div className={`${isDark ? styles.dark : ""} container`}>
        <div className={styles.pluginsHead}>
          <h2>Plugins</h2>
          <p>Could not load the plugin list — {greska}.</p>
        </div>
        <button className={styles.apply_btn} onClick={ucitaj}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className={`${isDark ? styles.dark : ""} container`}>
      <PluginsMain
        plugins={plugins}
        userPlugins={userPlugins}
        refreshData={ucitaj}
      />
    </div>
  );
};

export default Plugins;
